import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useRef,
} from 'react';

import { Typography } from '../typography';

import styles from './banner.scss';

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    title?: string;
    description?: string;
    titleFont?: string;
    descriptionFont?: string;
    image?: string;
    hero?: boolean;
    width?: string;
    withoutBackgroundDimming?: boolean;
    dataTestId?: string;
    onClick?: () => void;
  };

const COMPONENT_ID = 'modulor-banner';

export const Banner: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      title = '',
      description = '',
      image = '',
      hero = false,
      width,
      style,
      children,
      withoutBackgroundDimming = false,
      dataTestId = COMPONENT_ID,
      className,
      titleFont,
      descriptionFont,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const buttonRef = useRef<HTMLElement>(null);

    const classes = classNames(className, styles.banner, {
      [styles.hero]: image && hero,
      [styles.white]: !image || hero,
      [styles.dimming]: hero && !withoutBackgroundDimming,
      [styles.clickable]: typeof onClick === 'function',
    });

    const handleClick = (e: React.MouseEvent) => {
      if (typeof onClick === 'function' && !buttonRef.current.contains(e.target as Node)) onClick();
    };

    return (
      <div
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        data-testid={dataTestId}
        className={classes}
        onClick={(e) => handleClick(e)}
        ref={ref}
        style={{
          backgroundImage: hero && image ? `url('${image}')` : 'none',
          width,
          ...style,
        }}
        {...rest}>
        <div
          className={styles['right-image']}
          style={{
            backgroundImage: !hero && image ? `url('${image}')` : 'none',
          }}
        />
        <div className={styles['text-wrap']}>
          <Typography
            variant={titleFont || 'body-semibold'}
            data-test="title"
            dataTestId={`${dataTestId}-title`}
            className={styles['banner-typo']}>
            {title}
          </Typography>
          <Typography
            variant={descriptionFont || 'caption-1-regular'}
            data-test="description"
            dataTestId={`${dataTestId}-description`}
            className={styles['banner-typo']}>
            {description}
          </Typography>
        </div>
        <span ref={buttonRef} className={styles.button} data-testid={`${dataTestId}-buttons`}>
          {children}
        </span>
      </div>
    );
  },
);

Banner.displayName = 'Banner';
